const ATTEMPT_PREFIX = 'crm.feishu.login.';
const ENTRY_RECOVERY_KEY = 'crm.feishu.entry-recovery';

interface LoginAttempt {
  verifier: string;
  expiresAt: number;
  redirect: string;
}

export function isFeishuClient(userAgent = navigator.userAgent): boolean {
  return /\b(?:Feishu|Lark)\b/i.test(userAgent);
}

export function hasFeishuLoginCallback(): boolean {
  const query = new URLSearchParams(window.location.search);
  return Boolean(query.get('code') || query.get('error'));
}

export function claimFeishuEntryRecovery(
  code: string | null,
  state: string | null,
  error: string | null,
): boolean {
  // 工作台旧授权入口尚未创建 CRM 浏览器凭证，丢弃旧码后只自动重启一次。
  if (!isFeishuClient() || !code || error || /^[a-f0-9]{64}$/.test(state || ''))
    return false;
  const expiresAt = Number(sessionStorage.getItem(ENTRY_RECOVERY_KEY));
  if (Number.isFinite(expiresAt) && expiresAt > Date.now()) return false;
  sessionStorage.setItem(ENTRY_RECOVERY_KEY, String(Date.now() + 300000));
  return true;
}

export function redirectToFeishuAuthorization(url: string) {
  window.location.replace(url);
}

export function safeLoginRedirect(value: string | null): string {
  if (!value?.startsWith('/') || value.startsWith('//') || value.includes('\\'))
    return '/';
  const target = new URL(value, window.location.origin);
  if (
    target.origin !== window.location.origin ||
    ['/user/login', '/user/change-password'].includes(target.pathname)
  )
    return '/';
  return `${target.pathname}${target.search}${target.hash}`;
}

export async function createBrowserProof() {
  if (!window.crypto?.subtle)
    throw new Error('请通过 HTTPS 访问网站后使用飞书登录');
  const verifier = Array.from(
    window.crypto.getRandomValues(new Uint8Array(32)),
    (value) => value.toString(16).padStart(2, '0'),
  ).join('');
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  const challenge = Array.from(new Uint8Array(digest), (value) =>
    value.toString(16).padStart(2, '0'),
  ).join('');
  return { verifier, challenge };
}

export function saveFeishuAttempt(
  state: string,
  verifier: string,
  expiresIn: number,
) {
  // 每个标签页仅保留当前登录尝试，授权回跳仍在原标签页完成。
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(ATTEMPT_PREFIX)) sessionStorage.removeItem(key);
  }
  sessionStorage.setItem(
    `${ATTEMPT_PREFIX}${state}`,
    JSON.stringify({
      verifier,
      expiresAt: Date.now() + expiresIn * 1000,
      redirect: safeLoginRedirect(
        new URL(window.location.href).searchParams.get('redirect'),
      ),
    } satisfies LoginAttempt),
  );
}

export function discardFeishuAttempt(state: string) {
  sessionStorage.removeItem(`${ATTEMPT_PREFIX}${state}`);
}

export function consumeFeishuAttempt(state: string): LoginAttempt {
  if (!/^[a-f0-9]{64}$/.test(state))
    throw new Error('飞书登录已失效，请重新登录');
  const raw = sessionStorage.getItem(`${ATTEMPT_PREFIX}${state}`);
  discardFeishuAttempt(state);
  if (!raw) throw new Error('请在发起飞书登录的标签页完成登录');
  const attempt: LoginAttempt = JSON.parse(raw);
  if (
    !/^[a-f0-9]{64}$/.test(attempt.verifier) ||
    !Number.isFinite(attempt.expiresAt) ||
    attempt.expiresAt <= Date.now()
  )
    throw new Error('飞书登录已过期，请重新登录');
  sessionStorage.removeItem(ENTRY_RECOVERY_KEY);
  return { ...attempt, redirect: safeLoginRedirect(attempt.redirect) };
}
